import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import {
  DailyBalance,
  MonthlyBalance,
  PointBalance,
} from 'src/lib/entity/manage-point'
import { ConsoleLogger } from 'src/lib/console-logger'
import { PointType } from 'src/lib/constants'
import * as encoding from 'encoding-japanese'
import { saveAs } from 'file-saver'
import { ToastrService } from 'ngx-toastr'

type DailyBalancesForDisplay = {
  date: string
  paid: DailyBalance | string
  free: DailyBalance | string
}

type MonthlyBalancesForDisplay = {
  paid: MonthlyBalance | undefined
  free: MonthlyBalance | undefined
}

@Component({
  selector: 'maglive-admin-point-balance-index',
  templateUrl: './point-balance-index.component.html',
  styleUrls: [
    './point-balance-index.component.scss',
    '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss',
  ],
})
export class AdminPointBalanceIndexComponent implements OnInit {
  isShowDaily = false
  selectedMonth: string
  pointBalance: PointBalance
  monthlyBalances: MonthlyBalance[]
  monthlyBalancesForDisplay: MonthlyBalancesForDisplay | 'NOT_FOUND'
  dailyBalances: DailyBalance[]
  dailyBalancesForDisplay: DailyBalancesForDisplay[] | 'NOT_FOUND'
  changeDailyShow = false

  // データ取得フラグ
  isLoading: Boolean = false

  invalidCsvDl: Boolean

  constructor(
    private logger: ConsoleLogger,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    let date = new Date()
    this.selectedMonth =
      this.route.snapshot.queryParamMap.get('yearMonth') ||
      `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}`
    if (this.route.snapshot.queryParamMap.get('isShowDaily')) {
      this.isShowDaily =
        this.route.snapshot.queryParamMap.get('isShowDaily') === 'true'
    }
    this.isShowDaily
      ? (this.changeDailyShow = true)
      : (this.changeDailyShow = false)

    this.isLoading = true
    MagliveApiClient.fetchPointBalances(this.selectedMonth).subscribe(
      (value: any) => {
        this.logger.info(
          this.constructor.name,
          'ngOnInit',
          'fetchPointBalances',
          value
        )
        if (value !== 'NOT_FOUND') {
          this.pointBalance = value

          this.monthlyBalances = this.pointBalance.monthlyBalances
          if (this.monthlyBalances.length === 0) {
            this.monthlyBalancesForDisplay = 'NOT_FOUND'
          } else {
            this.monthlyBalancesForDisplay = this.convertMonthlyRecord()
          }

          this.dailyBalances = this.pointBalance.dailyBalances
          if (this.dailyBalances.length === 0) {
            this.dailyBalancesForDisplay = 'NOT_FOUND'
          } else {
            this.dailyBalancesForDisplay = this.convertDailyRecord()
          }
        } else {
          this.pointBalance = undefined
        }
        this.isLoading = false
      }
    )
  }

  execSearch(): void {
    if (this.isShowDaily) this.changeDailyShow = true
    else this.changeDailyShow = false
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        yearMonth: this.selectedMonth,
        isShowDaily: this.isShowDaily,
      },
    })

    this.isLoading = true
    MagliveApiClient.fetchPointBalances(this.selectedMonth).subscribe(
      (value: any) => {
        this.logger.info(
          this.constructor.name,
          'execSearch',
          'pointBalance',
          value
        )
        if (value !== 'NOT_FOUND') {
          this.pointBalance = value

          this.monthlyBalances = this.pointBalance.monthlyBalances
          if (this.monthlyBalances.length === 0) {
            this.monthlyBalancesForDisplay = 'NOT_FOUND'
          } else {
            this.monthlyBalancesForDisplay = this.convertMonthlyRecord()
          }

          this.dailyBalances = this.pointBalance.dailyBalances
          if (this.dailyBalances.length === 0) {
            this.dailyBalancesForDisplay = 'NOT_FOUND'
          } else {
            this.dailyBalancesForDisplay = this.convertDailyRecord()
          }
        } else {
          this.pointBalance = undefined
        }
        this.isLoading = false
      }
    )
  }

  update(): void {
    this.isLoading = true
    MagliveApiClient.updatePointBalances().subscribe((value: any) => {
      this.logger.info(
        this.constructor.name,
        'update',
        'updatePointBalances',
        value
      )
      if (value !== 'NOT_FOUND') {
        this.pointBalance = value
        this.monthlyBalances = this.pointBalance.monthlyBalances
        this.monthlyBalancesForDisplay = this.convertMonthlyRecord()
        this.dailyBalances = this.pointBalance.dailyBalances
        this.dailyBalancesForDisplay = this.convertDailyRecord()
      }
      const date = new Date()
      const newLocal = this
      newLocal.selectedMonth = `${date.getFullYear()} - ${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}`
      this.isLoading = false
    })
  }

  /**
   * 日毎の残高情報を表示し易い配列にまとめる
   */
  private convertDailyRecord(): DailyBalancesForDisplay[] {
    // 日付だけを抽出して重複を削除する
    let dates = Array.from(new Set(this.dailyBalances.map((obj) => obj.date)))
    // 結果格納用配列を準備
    let result: DailyBalancesForDisplay[] = Array.from({
      length: dates.length,
    }).map((_, i) => ({
      date: dates[i],
      paid: '',
      free: '',
    }))
    try {
      dates.forEach((date, index) => {
        this.dailyBalances.forEach((dailyBalance) => {
          if (dailyBalance.date === date && dailyBalance.pointType === 1) {
            result[index].paid = dailyBalance
          }
          if (dailyBalance.date === date && dailyBalance.pointType === 0) {
            result[index].free = dailyBalance
          }
        })
      })
      this.logger.info(
        this.constructor.name,
        'convertDailyRecord',
        'dailyBalances',
        result
      )
      return result
    } catch (e) {
      console.error(e)
    }
  }

  exportCsvPointBalance() {
    this.invalidCsvDl = true
    MagliveApiClient.exportCsvPointBalance(
      this.selectedMonth,
      this.isShowDaily
    ).subscribe(
      (value) => {
        if (value === 'NOT_FOUND') {
          this.invalidCsvDl = false
          this.toastr.error('エラーが発生しました。')
          return
        }
        if (!value.fileData.length) {
          this.invalidCsvDl = false
          this.toastr.error('ポイント残高データが存在していません。')
          return
        }

        const fileName = value.fileName
        const fileData = value.fileData

        const unicodeList = []
        for (let i = 0; i < fileData.length; i++) {
          unicodeList.push(fileData.charCodeAt(i))
        }

        const shiftJisCodeList = encoding.convert(
          unicodeList,
          'SJIS',
          'UNICODE'
        )
        const unit8Array = new Uint8Array(shiftJisCodeList)

        const blob = new Blob([unit8Array], { type: 'text/csv' })

        saveAs(blob, fileName)
        this.invalidCsvDl = false
      },
      (error) => {
        this.invalidCsvDl = false
        console.log(error)
      }
    )
  }

  /**
   * 月の残高情報を表示用配列にまとめる
   */
  private convertMonthlyRecord(): MonthlyBalancesForDisplay {
    return {
      paid: this.monthlyBalances.find(
        (elementAt) => elementAt.pointType === PointType.PAID
      ),
      free: this.monthlyBalances.find(
        (elementAt) => elementAt.pointType === PointType.FREE
      ),
    }
  }
}
